import React, { useState } from 'react';
import {
    formatNumberForDisplay,
    formatNumberForEdit,
    isValidIntermediateNumberInput,
    parseNumericValue,
    tryParseNumericValue,
} from '../../../utils/NumberUtils';

function initializeCellText(value: string): string {
    const initializedValue = value === '' ? '' : reformatNumericText(value, formatNumberForDisplay);
    return initializedValue;
}

function getElementTextContent(element: HTMLElement): string {
    return element.textContent?.trim() ?? '';
}

function removeTextSelectionRange(divElement: HTMLDivElement) {
    const selection = window.getSelection();
    if (!selection) return;
    let rangeToRemove: Range | null = null;
    for (let i = 0; i < selection.rangeCount; i++) {
        const range = selection.getRangeAt(i);
        if (range.commonAncestorContainer === divElement || range.commonAncestorContainer.parentNode === divElement) {
            rangeToRemove = range;
            break;
        }
    }
    if (rangeToRemove) selection.removeRange(rangeToRemove);
}

function getTextSelectionRange(divElement: HTMLDivElement): Range | null {
    let returnRange: Range | null = null;
    const selection = window.getSelection();
    if (!selection) return null;
    for (let i = 0; i < selection.rangeCount; i++) {
        const range = selection.getRangeAt(i);
        if (range.commonAncestorContainer === divElement) {
            if (divElement.childNodes.length > 0) {
                returnRange = new Range();
                returnRange.setStart(divElement.childNodes[0], 0);
                returnRange.setEnd(divElement.childNodes[0], getElementTextContent(divElement).length);
            } else {
                returnRange = range;
            }
        } else if (range.commonAncestorContainer.parentNode === divElement) {
            returnRange = range;
        }
        if (returnRange) break;
    }
    return returnRange;
}

function setTextSelectionRange(divElement: HTMLDivElement, start: number, end: number) {
    removeTextSelectionRange(divElement);
    const selection = window.getSelection();
    if (!selection) return;
    const newRange = document.createRange();
    if (divElement.textContent) {
        const textNode = divElement.childNodes[0];
        newRange.setStart(textNode, start);
        newRange.setEnd(textNode, end);
    } else {
        newRange.selectNode(divElement);
    }
    selection.addRange(newRange);
}

function reformatNumericText(value: string, numberFormatter: (v: number) => string): string {
    return numberFormatter(parseNumericValue(value, true));
}

type EditableNumberDivProps = React.HTMLAttributes<HTMLDivElement> & {
    value: string;
};

function cancelEvent(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
}

function handleInputNumber(event: Event) {
    const divElement: HTMLDivElement = event.target as HTMLDivElement;

    divElement.ariaLabel = reformatNumericText(getElementTextContent(divElement), formatNumberForDisplay);
}

function gethandleBlurNumberCallBack(
    currentCellText: string,
    setCellText: (value: string) => void,
    onBlur?: React.FocusEventHandler<HTMLDivElement>
): (event: React.FocusEvent<HTMLDivElement, Element>) => void {
    return (event: React.FocusEvent<HTMLDivElement, Element>) => {
        const divElement: HTMLDivElement = event.currentTarget as HTMLDivElement;
        const selection: Selection = window.getSelection() as Selection;

        // Remove event listeners for checking input before it is rendered and updating the aria label.
        divElement.removeEventListener('input', handleInputNumber);
        divElement.removeEventListener('beforeinput', handleBeforeInputNumber);

        // Clear any text selection within the number text.
        selection.removeAllRanges();

        const currentElementTextContent = getElementTextContent(divElement);
        const isZeroEquivalent: boolean = currentElementTextContent === '' || currentElementTextContent === '-';
        const newText: string = isZeroEquivalent ? '0' : reformatNumericText(currentElementTextContent, formatNumberForDisplay);

        divElement.removeAttribute('aria-label');
        divElement.textContent = newText;

        if (currentCellText !== newText) {
            setCellText(newText);

            if (onBlur) {
                onBlur(event);
            }
        }
    };
}

function handleBeforeInputNumber(event: InputEvent) {
    const div = event.target as HTMLDivElement;
    const selectionRange = getTextSelectionRange(div);
    if (!selectionRange) {
        event.preventDefault();
        event.stopPropagation();
        return;
    }

    const originalText = getElementTextContent(div);
    const before = originalText.substring(0, selectionRange.startOffset);
    const after = selectionRange.endOffset < originalText.length ? originalText.substring(selectionRange.endOffset) : '';
    const added = (event.data || event.dataTransfer?.getData('text') || '').trim();
    let beforeLength = before.length;
    let afterStartIndex = 0;

    switch (event.inputType) {
        case 'deleteContentBackward':
            if (selectionRange.collapsed && before.length > 0) beforeLength -= 1;
            break;
        case 'deleteContentForward':
            if (selectionRange.collapsed && after.length > 0) afterStartIndex = 1;
            break;
    }

    let newText = `${before.substring(0, beforeLength)}${added}${after.substring(afterStartIndex)}`;

    if (!isValidIntermediateNumberInput(newText)) {
        event.preventDefault();
        // Allow paste if it can be parsed to a number
        if (event.inputType === 'insertFromPaste' && tryParseNumericValue(newText, (result) => (newText = formatNumberForEdit(result)))) {
            div.textContent = newText;
            const caretPosition = newText.length - after.substring(afterStartIndex).length;
            setTextSelectionRange(div, caretPosition, caretPosition);
        }
    }

    event.stopPropagation();
}

function handleKeyDownKeyUp(event: React.KeyboardEvent<HTMLDivElement>) {
    // Ensure text editing navigation does not get overridden by standard grid navigation.
    switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowRight':
        case 'End':
        case 'Home':
            event.stopPropagation();
            break;
    }
}

function handleMouseDownMouseUp(event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    const divElement: HTMLDivElement = event.currentTarget as HTMLDivElement;

    if (document.activeElement !== divElement) {
        event.preventDefault();
        divElement.focus();
    }
}

export const EditableNumberDiv: React.FC<EditableNumberDivProps> = (props: EditableNumberDivProps) => {
    const [cellText, setCellText] = useState<string>(initializeCellText(props.value));

    function handleFocusNumber(event: React.FocusEvent<HTMLDivElement, Element>) {
        const divElement: HTMLDivElement = event.currentTarget as HTMLDivElement;
        const selection: Selection = window.getSelection() as Selection;
        const range: Range = document.createRange();

        // Remove all formatting from the number text.
        divElement.ariaLabel = divElement.textContent;
        divElement.textContent = reformatNumericText(getElementTextContent(divElement), formatNumberForEdit);

        // Select the number text
        range.selectNode(divElement.childNodes[0]);
        selection.removeAllRanges();
        selection.addRange(range);

        // Add event listeners for checking input before it is rendered and updating the cell aria label.
        divElement.addEventListener('beforeinput', handleBeforeInputNumber);
        divElement.addEventListener('input', handleInputNumber);
    }

    const eventProps = {
        onDrag: cancelEvent,
        onDragEnd: cancelEvent,
        onDragOver: cancelEvent,
        onDragStart: cancelEvent,
        onDrop: cancelEvent,
        onMouseDown: handleMouseDownMouseUp,
        onMouseUp: handleMouseDownMouseUp,
        onKeyDown: handleKeyDownKeyUp,
        onKeyUp: handleKeyDownKeyUp,
        onFocus: handleFocusNumber,
        onBlur: gethandleBlurNumberCallBack(cellText, setCellText, props.onBlur),
    };

    return (
        <div
            {...props}
            role={props.role || 'textbox'}
            aria-label={cellText}
            suppressContentEditableWarning
            contentEditable='plaintext-only'
            className={props.className}
            {...eventProps}
            dangerouslySetInnerHTML={{ __html: cellText }}
        />
    );
};
